import React, { useState, useEffect } from 'react';
import { FormConfig, SubmissionData } from '../types';
import { FormField } from './FormField';
import { ProgressBar } from './ProgressBar/ProgressBar';
import { validateForm } from '../utils/validation';
import { submitForm, fileToBase64 } from '../utils/api';
import { useFormAutoSave } from '../hooks/useFormAutoSave';
import { useFieldTracking } from '../hooks/useFieldTracking';
import { usePartialDataCapture } from '../hooks/usePartialDataCapture';
import { initI18n, t } from '../utils/i18n';

interface FormRendererProps {
  formConfig: FormConfig;
  onSubmit?: (data: SubmissionData) => Promise<void>;
  onSuccess?: (response: any) => void;
  onError?: (error: Error) => void;
  disabled?: boolean;
}

/** Construye las CSS custom properties del tema para el contenedor raíz. */
function buildThemeCssVars(theme: FormConfig['theme']): React.CSSProperties {
  const vars: Record<string, string> = {};

  if (theme.primaryColor)        vars['--hexaforms-primary']     = theme.primaryColor;
  if (theme.backgroundColor)     vars['--hexaforms-bg']          = theme.backgroundColor;
  if (theme.textColor)           vars['--hexaforms-text']        = theme.textColor;
  if (theme.labelColor)          vars['--hexaforms-label']       = theme.labelColor;
  if (theme.inputBackgroundColor) vars['--hexaforms-input-bg']   = theme.inputBackgroundColor;
  if (theme.borderColor)         vars['--hexaforms-border']      = theme.borderColor;
  if (theme.placeholderColor)    vars['--hexaforms-placeholder'] = theme.placeholderColor;
  if (theme.textLightColor)      vars['--hexaforms-text-light']  = theme.textLightColor;
  if (theme.bgLightColor)        vars['--hexaforms-bg-light']    = theme.bgLightColor;
  if (theme.fontFamily)          vars['--hexaforms-font']        = theme.fontFamily;

  return vars as React.CSSProperties;
}

export const FormRenderer: React.FC<FormRendererProps> = ({
  formConfig,
  onSubmit,
  onSuccess,
  onError,
  disabled: externalDisabled,
}) => {
  const [formData, setFormData] = useState<Record<string, any>>({});
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [showDraftRecovery, setShowDraftRecovery] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  const disabled = externalDisabled || isSubmitting;

  // Multi-step logic
  const isMultiStep = formConfig.isMultiStep && formConfig.steps && formConfig.steps.length > 1;
  const steps = formConfig.steps || [];
  const currentStepFields = isMultiStep
    ? formConfig.fields.filter(field =>
        steps[currentStep]?.fieldIds.includes(field.fieldId)
      )
    : formConfig.fields;

  // Hooks
  const { loadDraft, clearDraft } = useFormAutoSave({
    formId: formConfig.formId,
    data: formData,
    enabled: true,
  });

  const { trackFieldInteraction, trackSessionStart, sendFunnelData } = useFieldTracking({
    formId: formConfig.formId,
    enabled: true,
  });

  const { partialData, captureFieldData } = usePartialDataCapture({
    formId: formConfig.formId,
    formData,
    formFields: formConfig.fields,
    enabled: true,
  });

  // Antes de pintar: si se hace en un efecto, el primer render sale en el idioma anterior.
  initI18n(formConfig.language);
  useEffect(() => { trackSessionStart(); }, [trackSessionStart]);

  useEffect(() => {
    const draft = loadDraft();
    if (draft && Object.keys(draft).length > 0) {
      setShowDraftRecovery(true);
    }
  }, [loadDraft]);

  const handleRecoverDraft = () => {
    const draft = loadDraft();
    if (draft) {
      setFormData(draft);
      setShowDraftRecovery(false);
    }
  };

  const handleDiscardDraft = () => {
    clearDraft();
    setShowDraftRecovery(false);
  };

  const handleFieldChange = (fieldId: string, value: any) => {
    trackFieldInteraction(fieldId, 'change', value);
    captureFieldData(fieldId, value);
    setFormData(prev => ({ ...prev, [fieldId]: value }));
    if (errors[fieldId]) {
      setErrors(prev => {
        const next = { ...prev };
        delete next[fieldId];
        return next;
      });
    }
  };

  const handleFieldFocus = (fieldId: string) => trackFieldInteraction(fieldId, 'focus');

  const handleFieldBlur = (fieldId: string) => {
    trackFieldInteraction(fieldId, 'blur');
    const value = formData[fieldId];
    if (value !== undefined && value !== null && value !== '') {
      captureFieldData(fieldId, value);
    }
  };

  // Multi-step navigation
  const handleNextStep = () => {
    if (!isMultiStep || currentStep >= steps.length - 1) return;
    const stepFields = formConfig.fields.filter(f =>
      steps[currentStep]?.fieldIds.includes(f.fieldId)
    );
    const stepErrors = validateForm(stepFields, formData);
    if (Object.keys(stepErrors).length > 0) {
      setErrors(stepErrors);
      return;
    }
    setCurrentStep(prev => prev + 1);
    setErrors({});
  };

  const handlePreviousStep = () => {
    if (isMultiStep && currentStep > 0) {
      setCurrentStep(prev => prev - 1);
      setErrors({});
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitStatus('idle');

    const validationErrors = validateForm(formConfig.fields, formData);
    if (Object.keys(validationErrors).length > 0) {
      setErrors(validationErrors);
      if (isMultiStep) {
        const firstErrorField = Object.keys(validationErrors)[0];
        const errorStepIndex = steps.findIndex(s => s.fieldIds.includes(firstErrorField));
        if (errorStepIndex !== -1) setCurrentStep(errorStepIndex);
      }
      return;
    }

    setIsSubmitting(true);

    try {
      const files = await Promise.all(
        formConfig.fields
          .filter(field => field.componentType === 'file_upload' && formData[field.fieldId])
          .map(async field => {
            const file = formData[field.fieldId] as File;
            const maxSize = field.config?.maxSize || 5242880;
            if (file.size > maxSize) {
              throw new Error(`El archivo "${file.name}" supera el tamaño máximo (${(maxSize / 1024 / 1024).toFixed(1)}MB)`);
            }
            const allowedTypes = field.config?.allowedTypes || [];
            if (allowedTypes.length > 0 && !allowedTypes.includes(file.type)) {
              throw new Error(`Tipo de archivo no permitido para "${file.name}"`);
            }
            return {
              field_id: field.fieldId,
              filename: file.name,
              content: await fileToBase64(file),
              content_type: file.type,
            };
          })
      );

      const submissionData: SubmissionData = {
        data: formData,
        files: files.length > 0 ? files : undefined,
      };

      if (onSubmit) {
        await onSubmit(submissionData);
      } else {
        await submitForm(formConfig.formId, formConfig.apiKey, submissionData);
      }

      sendFunnelData('completed');
      clearDraft();
      setSubmitStatus('success');
      onSuccess?.({ message: 'Form submitted successfully' });
    } catch (error) {
      setSubmitStatus('error');
      const err = error instanceof Error ? error : new Error('Submission failed');
      onError?.(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  useEffect(() => {
    return () => {
      if (submitStatus !== 'success') {
        sendFunnelData('abandoned', partialData);
      }
    };
  }, [sendFunnelData, submitStatus, partialData]);

  const theme = formConfig.theme;
  const primaryColor = theme.primaryColor || '#4F46E5';
  const buttonStyle = theme.buttonStyle || 'rounded';
  const themeCssVars = buildThemeCssVars(theme);

  // ─── Estado: éxito ─────────────────────────────────────────────────────────
  if (submitStatus === 'success') {
    return (
      <div className="hexaforms-root hexaforms-success" style={themeCssVars}>
        <div className="hexaforms-success-icon">✓</div>
        <h2 className="hexaforms-success-title">{t('success.title')}</h2>
        <p className="hexaforms-success-message">
          {formConfig.settings.successMessage || t('success.message')}
        </p>
      </div>
    );
  }

  // Campos del paso actual, ordenados
  const fieldsToDisplay = [...(isMultiStep ? currentStepFields : formConfig.fields)]
    .sort((a, b) => (a.order || 0) - (b.order || 0));

  return (
    <div className="hexaforms-root" style={themeCssVars}>
      {/* Progress Bar (multi-step) */}
      {isMultiStep && steps.length > 1 && (
        <ProgressBar current={currentStep + 1} total={steps.length} theme={theme} />
      )}

      {/* Draft recovery */}
      {showDraftRecovery && (
        <div className="hexaforms-draft-recovery">
          <span>{t('draft.found')}</span>
          <div className="hexaforms-draft-actions">
            <button
              className="hexaforms-draft-btn hexaforms-draft-btn--recover"
              onClick={handleRecoverDraft}
            >
              {t('draft.recover')}
            </button>
            <button
              className="hexaforms-draft-btn hexaforms-draft-btn--discard"
              onClick={handleDiscardDraft}
            >
              {t('draft.discard')}
            </button>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="hexaforms-form">
        {/* Campos */}
        {fieldsToDisplay.map(field => {
          const colSpan = (field.config?.colSpan as number) ?? 2;
          return (
            <div key={field.fieldId} style={colSpan === 1 ? {gridColumn:'span 1'} : undefined}>
              <FormField
                field={field}
                value={formData[field.fieldId]}
                onChange={value => handleFieldChange(field.fieldId, value)}
                onFocus={() => handleFieldFocus(field.fieldId)}
                onBlur={() => handleFieldBlur(field.fieldId)}
                error={errors[field.fieldId]}
                theme={theme}
                disabled={disabled}
              />
            </div>
          );
        })}

        {/* Error de submit */}
        {submitStatus === 'error' && (
          <div className="hexaforms-submit-error">
            {t('error.submit')}
          </div>
        )}

        {/* Navegación multi-paso */}
        {isMultiStep && steps.length > 1 && (
          <div className="hexaforms-step-nav">
            <button
              type="button"
              onClick={handlePreviousStep}
              disabled={currentStep === 0 || disabled}
              className="hexaforms-step-btn hexaforms-step-btn--prev"
              style={{ '--hexaforms-primary': primaryColor } as React.CSSProperties}
            >
              {t('navigation.previous')}
            </button>

            {currentStep < steps.length - 1 ? (
              <button
                type="button"
                onClick={handleNextStep}
                disabled={disabled}
                className="hexaforms-step-btn hexaforms-step-btn--next"
              >
                {t('navigation.next')}
              </button>
            ) : (
              <button
                type="submit"
                disabled={disabled}
                className={`hexaforms-submit-button hexaforms-button-${buttonStyle}`}
              >
                {isSubmitting ? t('submitting') : (formConfig.settings.submitButtonText || t('submit'))}
              </button>
            )}
          </div>
        )}

        {/* Botón submit (single-step) */}
        {!isMultiStep && (
          <button
            type="submit"
            disabled={disabled}
            className={`hexaforms-submit-button hexaforms-button-${buttonStyle}`}
          >
            {isSubmitting ? t('submitting') : (formConfig.settings.submitButtonText || t('submit'))}
          </button>
        )}
      </form>

      <p style={{
        marginTop: '1rem',
        textAlign: 'center',
        fontSize: '0.75rem',
        opacity: 0.4,
        letterSpacing: '0.02em',
      }}>
        Powered by <strong>HexaForms</strong>
      </p>
    </div>
  );
};
